import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { DateTime } from "luxon";
import { Show } from "shared/components";
import { Banner } from "shared/components/Banner";
import { cx } from "shared/utils";
import { AH_LIMIT_REACHED_MESSAGE } from "views/settings-integration/integrations/affordable-housing-com/constants";
import type { AhLeadUsage } from "views/settings-integration/integrations/affordable-housing-com/useAhLeadUsage";

type UsageLevel = "normal" | "nearing" | "reached";

const LEVEL_STYLES: Record<
  UsageLevel,
  { text: string; bar: string; label?: string }
> = {
  normal: { text: "text-content-1000", bar: "bg-primary-500" },
  nearing: {
    text: "text-warning-700",
    bar: "bg-warning-500",
    label: "Nearing limit"
  },
  reached: {
    text: "text-danger-700",
    bar: "bg-danger-500",
    label: "Limit reached"
  }
};

const getUsageLevel = ({
  count,
  limit,
  isLimitReached
}: AhLeadUsage): UsageLevel => {
  if (isLimitReached) return "reached";
  if (count === limit - 1) return "nearing";
  return "normal";
};

// resets_on is a calendar date, so parse it as one — no timezone shift.
const formatResetDate = (resetsOn: string) =>
  DateTime.fromISO(resetsOn).toFormat("LLL d");

const limitReachedIcon = (
  <HugeiconsIcon icon={AlertCircleIcon} size={20} className="text-danger-600" />
);

interface AhLeadUsageTrackerProps {
  usage: AhLeadUsage;
}

export const AhLeadUsageTracker = ({ usage }: AhLeadUsageTrackerProps) => {
  const { count, limit, isLimitReached, resetsOn } = usage;
  const styles = LEVEL_STYLES[getUsageLevel(usage)];
  const fillPercent = limit > 0 ? Math.min(count / limit, 1) * 100 : 0;

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <p className={cx("text-sm font-bold", styles.text)}>
          {count} of {limit} leads this month
        </p>
        <Show if={Boolean(styles.label)}>
          <span className={cx("text-xs font-bold", styles.text)}>
            {styles.label}
          </span>
        </Show>
      </div>

      <div className="h-2 w-full rounded-lg bg-background-800 overflow-hidden">
        <div
          className={cx(
            "h-full rounded-lg transition-all duration-300",
            styles.bar
          )}
          style={{ width: `${fillPercent}%` }}
        />
      </div>

      <Show if={Boolean(resetsOn)}>
        <p className="text-xs text-content-300">
          Resets {resetsOn ? formatResetDate(resetsOn) : ""}
        </p>
      </Show>

      <Show if={isLimitReached}>
        <Banner
          variant="danger"
          textTheme="colorful"
          borders="none"
          HugeIcon={limitReachedIcon}
          body={AH_LIMIT_REACHED_MESSAGE}
        />
      </Show>
    </div>
  );
};
